import { useMemo } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { Column } from 'react-table'

import { IncidentEvent } from '@/graphql/generated/schemas'

import { columns } from '../components/Columns'
import {
  BULK_ACTION_CHECKBOX_COL_ID,
  DATE_COL_NAME,
  DISPLAY_ID_COL_NAME,
  FACILITY_COL_NAME,
  FLOOR_COL_NAME,
  GUARD_COL_NAME,
  INCIDENT_NAME_COL_NAME,
  IS_FLAGGED_COL_NAME,
  OPERATOR_COL_NAME,
  PRIORITY_COL_NAME,
  STATUS_OWNER_COL_NAME,
} from '../constants/columnIds'

// Columns that lose sortability while Priority Queue's pinned two-key sort is
// active, leaving Date as the only remaining clickable sort.
const COLUMNS_DISABLED_WHILE_PRIORITY_QUEUE_ACTIVE = [
  DISPLAY_ID_COL_NAME,
  INCIDENT_NAME_COL_NAME,
  STATUS_OWNER_COL_NAME,
  OPERATOR_COL_NAME,
  GUARD_COL_NAME,
]

// ^1/^2 badges shown next to the primary (Priority) and secondary (Date) sort.
const PRIMARY_SORT_BADGE = 1 as const
const SECONDARY_SORT_BADGE = 2 as const

interface UseFilteredIncidentColumnsIProps {
  isPriorityQueueActive?: boolean
}

export const useFilteredIncidentColumns = ({
  isPriorityQueueActive = false,
}: UseFilteredIncidentColumnsIProps = {}) => {
  const isBulkActionEnabled = useFeatureFlag(FeatureFlag.bulkIncidentActions)
  const isAiOperatorEnabled = useFeatureFlag(FeatureFlag.aiOperator)
  const isPriorityQueueEnabled = useFeatureFlag(FeatureFlag.priorityQueue)

  const filteredColumns = useMemo(() => {
    const excludedColumns = []

    // If AI Operator is not enabled => flag column should be hidden
    // Note: Keep the flag column visible when AI Operator is enabled, even if no incidents are currently flagged
    // This prevents column shifting when flagged incidents appear/disappear
    if (!isAiOperatorEnabled) {
      excludedColumns.push(IS_FLAGGED_COL_NAME)
    }

    // If Bulk Resolve is not enabled => action checkbox column should be hidden
    if (!isBulkActionEnabled) {
      excludedColumns.push(BULK_ACTION_CHECKBOX_COL_ID)
    }

    const withExclusions = filterByColumnNamesOrIds(columns, excludedColumns)

    if (!isPriorityQueueEnabled) return withExclusions

    return withExclusions.map((col) => {
      const id = col?.id ?? col?.accessorKey

      if (id === FACILITY_COL_NAME || id === FLOOR_COL_NAME) {
        return { ...col, enableSorting: false }
      }

      if (!isPriorityQueueActive) return col

      // Priority is the primary sort key, but it's pinned rather than a real
      // clickable sort: there's no direction to toggle (always highest-tier
      // first), so it's marked non-sortable and its header instead reads
      // shouldForceSortedDesc/sortOrderBadge to render a fixed "^1" desc
      // indicator. Date is the real (secondary) sort left clickable, badged "^2".
      if (id === PRIORITY_COL_NAME) {
        return {
          ...col,
          enableSorting: false,
          meta: {
            ...col.meta,
            shouldForceSortedDesc: true,
            sortOrderBadge: PRIMARY_SORT_BADGE,
          },
        }
      }
      if (id === DATE_COL_NAME) {
        return {
          ...col,
          meta: { ...col.meta, sortOrderBadge: SECONDARY_SORT_BADGE },
        }
      }
      if (COLUMNS_DISABLED_WHILE_PRIORITY_QUEUE_ACTIVE.includes(id)) {
        return { ...col, enableSorting: false }
      }
      return col
    })
  }, [
    isAiOperatorEnabled,
    isBulkActionEnabled,
    isPriorityQueueEnabled,
    isPriorityQueueActive,
  ])

  return filteredColumns
}

export const filterByColumnNamesOrIds = (
  columns: Column<IncidentEvent>[],
  excludedColumns: string[]
): Column<IncidentEvent>[] => {
  return columns.filter(
    (col) =>
      !excludedColumns.includes(col?.accessorKey) &&
      !excludedColumns.includes(col?.id)
  )
}
